import { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { BUSINESS } from "@/data/content";
import UCLogo from "@/components/site/UCLogo";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Work", href: "#work" },
  { label: "Process", href: "#process" },
  { label: "Contact", href: "#contact" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-background/90 backdrop-blur-md border-b border-black/10" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-3 group">
          <UCLogo className={`h-9 w-9 sm:h-11 sm:w-11 ${scrolled ? "text-concrete-900" : "text-white"}`} />
          <span className="hidden sm:block leading-none">
            <span className={`block font-display font-extrabold tracking-tight text-sm ${scrolled ? "text-concrete-900" : "text-white"}`}>UPSCALE CONCRETE</span>
            <span className={`overline ${scrolled ? "text-concrete-500" : "text-white/70"}`}>Services</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={`overline transition-colors ${scrolled ? "text-concrete-700 hover:text-concrete-900" : "text-white/80 hover:text-white"}`}
            >
              {l.label}
            </a>
          ))}
          <a
            href={`tel:${BUSINESS.phoneIntl}`}
            data-testid="nav-call-btn"
            className={`group flex items-center gap-2 px-5 py-2.5 text-sm font-medium border transition-colors ${scrolled ? "bg-concrete-900 text-white border-concrete-900 hover:bg-white hover:text-concrete-900" : "bg-white text-concrete-900 border-white hover:bg-transparent hover:text-white"}`}
          >
            <Phone className="h-4 w-4" />
            {BUSINESS.phone}
          </a>
        </nav>

        <button
          className={`md:hidden ${scrolled ? "text-concrete-900" : "text-white"}`}
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-background border-b border-black/10 px-5 pb-6 pt-2" data-testid="nav-mobile-menu">
          <div className="flex flex-col">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="py-3 border-b border-black/10 font-display font-semibold text-lg text-concrete-900"
              >
                {l.label}
              </a>
            ))}
            <a
              href={`tel:${BUSINESS.phoneIntl}`}
              className="mt-4 flex items-center justify-center gap-2 bg-concrete-900 text-white px-5 py-3 font-medium"
            >
              <Phone className="h-4 w-4" /> Call {BUSINESS.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
